import { useContext, useEffect, useMemo, useRef } from "react";

import About from "../../components/marketing/engineering/About";
import BlogPreview from "../../components/marketing/BlogPreview";
import Footer from "../../components/marketing/engineering/Footer";
import Hero from "../../components/marketing/engineering/Hero";
import Impact from "../../components/marketing/engineering/Impact";
import Manifesto from "../../components/marketing/engineering/Manifesto";
import PrivateEquity from "../../components/marketing/engineering/PrivateEquity";
import Problems from "../../components/marketing/engineering/Problems";
import Services from "../../components/marketing/engineering/Services";
import Work from "../../components/marketing/engineering/Work";
import Seo from "../../components/seo/Seo";
import { blogPosts } from "../../content/blogPosts";
import { siteMetadata } from "../../content/siteMetadata";
import { ApplicationContext } from "../../context/GlobalState";
import { setActiveSection, setPageMetadata } from "../../context/actions";
import MarketingLayout from "../../layouts/MarketingLayout";
import { sortBlogPosts } from "../../utils/siteUtils";

const Engineering = () => {
  const heroRef = useRef(null);
  const [state, dispatch] = useContext(ApplicationContext);

  const latestPosts = useMemo(
    () => sortBlogPosts(blogPosts).slice(0, 3),
    []
  );

  useEffect(() => {
    dispatch(
      setPageMetadata({
        title: "Forward-Deployed Engineering | Data & AI Services for Healthcare & Private Equity | Data Chimp Labs",
        description:
          "We embed elite data & AI engineering teams directly into healthcare and private equity organizations. Fractional CDO, HIPAA-compliant AI products, cloud data platforms, and workflow automation — delivered in weeks, not months.",
        image: siteMetadata.socialImage,
        pathname: "/forward-deployed-engineering",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const sectionIds = ["problems", "pe", "services", "work", "about", "blog"];
    const observedElements = sectionIds
      .map((sectionId) => document.getElementById(sectionId))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.find((entry) => entry.isIntersecting);

        if (visibleEntry?.target?.id) {
          dispatch(setActiveSection(visibleEntry.target.id));
        }
      },
      {
        threshold: 0.3,
        rootMargin: "-20% 0px -55% 0px",
      }
    );

    observedElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [dispatch]);

  return (
    <>
      <Seo
        title="Forward-Deployed Engineering | Data & AI Services for Healthcare & Private Equity | Data Chimp Labs"
        description="We embed elite data & AI engineering teams directly into healthcare and private equity organizations. Fractional CDO, HIPAA-compliant AI products, cloud data platforms, and workflow automation — delivered in weeks, not months."
        pathname="/forward-deployed-engineering"
        image={siteMetadata.socialImage}
        imageAlt="Data Chimp Labs — Forward-Deployed Data & AI Engineering for Healthcare & Private Equity"
        keywords={[
          "forward-deployed engineering",
          "data engineering healthcare",
          "ai engineering team",
          "fractional cdo",
          "hipaa compliant ai development",
          "healthcare data pipelines",
          "private equity data engineering",
          "embedded engineering team",
          "production ai deployment",
          "healthcare workflow automation",
          "data chimp labs engineering",
        ]}
      />

      <MarketingLayout heroRef={heroRef} footer={<Footer />}>
        <Hero
          heroRef={heroRef}
          prefersReducedMotion={state.prefersReducedMotion}
        />
        <Manifesto />
        <div className="glow-line" />
        <Problems />
        <div className="glow-line" />
        <PrivateEquity />
        <div className="glow-line" />
        <Impact />
        <div className="glow-line" />
        <Services />
        <div className="glow-line" />
        <Work />
        <div className="glow-line" />
        <About />
        <div className="glow-line" />
        <BlogPreview posts={latestPosts} />
      </MarketingLayout>
    </>
  );
};

export default Engineering;
